import {
  Box,
  Typography,
  Stack,
  Button,
  FormControl,
  FormLabel,
  Input,
  Switch,
  Card,
  CardContent,
  Chip
} from '@mui/joy';
import useUser from './auth/useUser';
import { useNavigate, useParams, useLocation } from 'react-router';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useFeedback } from './feedback/Service';
import { parseGraphQLError } from '../utils';
import EditIcon from '@mui/icons-material/Edit';
import ViewIcon from '@mui/icons-material/Visibility';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import FormConstructor from './FormConstructor';
import { CORE_JOB_FIELDS } from '../gql/fragments/job.fragment';
import useUploadPdf from './useUploadPdf';
import FormRenderer from './FormRenderer';
import { ContentCopy, OpenInNew } from '@mui/icons-material';
import { DEFAULT_QUESTIONNAIRE } from '../constants';
import InterviewQuestionsEditor from './InterviewQuestionsEditor';
import useUnsavedChangesGuard from './useUnsavedChangesGuard';
import DefaultInterviewQuestionsSelector from './DefaultInterviewQuestionsSelector';
import useTracking from './useTracking';
import POSITIONS_QUERY from '../gql/admin/positions.query';
import { parseQuestions } from './DefaultInterviewQuestionsSelector';
import { ATSJobLinker } from './integrations';
import JobDescriptionInput, { getDescriptionValidationError, isParsePending } from './JobDescriptionInput';
import useJobParsePolling from './useJobParsePolling';

const transformPosition = (position) => {
  const questions = (position.defaultQuestions || []).map(dq => ({
    sectionId: dq.sectionId,
    sectionTitle: dq.section?.name,
    maxQuestions: dq.section?.maxQuestions || (dq.questions?.length || 0),
    questionType: dq.section?.questionType,
    questions: parseQuestions(dq)
  }));
  return {
    id: position._id,
    title: position.title,
    questions
  };
};

const formatData = (data, isAtsMode) => {
  data.atsTargetStageId = undefined;
  data.atsSourceStageId = undefined;
  data.ashbyFlowMode = undefined;
  data.greenhouseFlowMode = undefined;
  data.jobDescriptionStatus = undefined;
  data.jobDescriptionError = undefined;
  data.hasJobDescription = undefined;
  data.userEditedDescription = undefined;
  const trimmedDescription = (data.jobDescription || '').trim();
  data.jobDescription = trimmedDescription || undefined;
  data.questions = JSON.stringify(isAtsMode ? [] : data.questions);
  data.interviewQuestions = JSON.stringify([...data.interviewQuestions].map(({ sectionId, questions, prefix }) => ({ sectionId, questions, prefix })));
  return data;
}

const buildTemplates = (positions) => (positions || []).map(p => transformPosition(p));

const POST_JOB = gql`
  ${CORE_JOB_FIELDS}
  mutation PostJob($job: JobInput!) {
    postJob(job: $job) {
      ...CoreJobFields
    }
  }
`;

const DELETE_JOB = gql`
  ${CORE_JOB_FIELDS}
  mutation deleteJob($_id: String!) {
    deleteJob(_id: $_id) {
      _id
      jobs {
        ...CoreJobFields
      }
    }
  }
`;

const initialState = {
  title: "",
  pdfUrl: "",
  jobDescription: "",
  questions: DEFAULT_QUESTIONNAIRE,
  published: true,
  comment: "",
  hideClientName: false,
  interviewQuestions: [],
  userEditedDescription: false,
}

const reducer = (state, newState) => ({
  ...state,
  ...newState
});

const getStateFromInitialData = (initialData, defaultInterviewQuestions = []) => {
  if (!initialData) return { ...initialState };
  return {
    ...initialData,
    jobDescription: initialData?.jobDescription || "",
    comment: initialData?.comment || "",
    hideClientName: initialData?.hideClientName || false,
    questions: initialData?.questions ? JSON.parse(initialData.questions) : [],
    interviewQuestions: parseInterviewQuestions(initialData?.interviewQuestions, defaultInterviewQuestions),
    userEditedDescription: false,
    applications: undefined,
    allApplications: undefined,
    hasJobDescription: undefined,
    candidatesStats: undefined,
    createdAt: undefined,
    updatedAt: undefined,
    __typename: undefined,
  };
}

const parseInterviewQuestions = (interviewQuestionsString, defaultInterviewQuestions = []) => {
  if (!interviewQuestionsString) return defaultInterviewQuestions;
  try {
    const parsed = JSON.parse(interviewQuestionsString);
    return parsed.map(section => {
      const { questions } = section;
      const defaultSection = defaultInterviewQuestions.find(x => x.sectionId === section.sectionId);
      return {
        ...defaultSection,
        questions
      };
    });
  } catch (error) {
    console.error("Error parsing interview questions", error);
    return defaultInterviewQuestions;
  }
}

const Job = () => {
  const [state, setStateInternal] = useReducer(reducer, initialState);
  const { jobId } = useParams();
  const location = useLocation();
  const { user, loading, refetch } = useUser();
  const [showFormEdit, setShowFormEdit] = useState(false);
  const [showInterviewEdit, setShowInterviewEdit] = useState(false);
  const initialData = useMemo(() => (user?.client?.jobs || []).find(x => x._id === jobId), [user, jobId]);
  const feedback = useFeedback();
  const navigate = useNavigate();
  const [uploadObject, uploadPdf, resetUpload] = useUploadPdf();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { data: positionsData } = useQuery(POSITIONS_QUERY, { variables: { limit: 200 } });
  const { trackCopyJobUrl, trackOpenDemo } = useTracking();
  const isAtsMode = user?.client?.usesATS === true && !!user?.client?.selectedATSProvider;
  const loadedJobIdRef = useRef(null);
  const originalPdfUrlRef = useRef(null);

  const templates = useMemo(() => buildTemplates(positionsData?.positions || []), [positionsData]);
  const defaultInterviewQuestions = useMemo(() => templates[0]?.questions || [], [templates]);

  const setState = (newState) => {
    setStateInternal(newState);
    setHasUnsavedChanges(true);
  };

  const interviewQuestionsTemplate = useMemo(() => {
    const tpl = templates.find(x => JSON.stringify(x.questions) === JSON.stringify(state.interviewQuestions));
    return tpl ? "default for " + tpl.title : "custom questions";
  }, [state.interviewQuestions, templates]);

  // Replace large inline navigation guard logic with reusable hook
  useUnsavedChangesGuard(hasUnsavedChanges, feedback.confirm);

  const [submit, { loading: posting }] = useMutation(POST_JOB, {
    update(cache, { data: { postJob } }) {
      console.log("postJob", postJob);
      cache.modify({
        id: cache.identify({ _id: user.client._id, __typename: "Client" }),
        fields: {
          jobs: () => postJob,
        }
      });
      if (!initialData?._id) {
        setHasUnsavedChanges(false);
        navigate("/job/" + postJob[postJob.length - 1]._id, { replace: true, state: { justCreated: true } });
      }
    }
  });

  const [remove, { loading: deleting }] = useMutation(DELETE_JOB, {
    update(cache, { data: { deleteJob: client } }) {
      console.log("deleteJob result", client);
      cache.modify({
        fields: {
          jobs(current, { readField }) {
            console.log("current", current)
            return current.filter(x => jobId !== readField('_id', x));
          },
        },
      });
    }
  });

  // Suppress a brief "not found" flash right after navigation from a successful creation
  const justCreated = !!location.state?.justCreated;
  const isNotFound = jobId && jobId !== "new" && !initialData && user && !loading && !posting && !justCreated;

  const handleDeleteClick = async e => {
    e.stopPropagation();
    const confirm = await feedback.confirm({ title: "Remove posted job?" });
    if (!confirm)
      return;
    try {
      setHasUnsavedChanges(false);
      await remove(({ variables: { _id: jobId } }));
      feedback.snackbar({ text: "Job removed!", type: "success" });
      navigate("/jobs", { replace: true });
    } catch (error) {
      console.error("Delete request error", error);
      feedback.snackbar({ text: parseGraphQLError(error), type: "error" });
    }
  }

  const handleSubmit = async e => {
    e.preventDefault();
    const pdfChanged = (state.pdfUrl || '') !== (originalPdfUrlRef.current || '');
    const descErr = getDescriptionValidationError({ ...state, pdfChanged });
    if (descErr) {
      feedback.snackbar({ text: descErr, type: "error" });
      return;
    }
    try {
      let data = { ...state };
      data = formatData(data, isAtsMode);
      const { data: { postJob: newJob } } = await submit(({ variables: { job: data } }));
      const savedJobs = Array.isArray(newJob) ? newJob : [newJob].filter(Boolean);
      const savedJob = savedJobs.find(j => j._id === jobId) || savedJobs[savedJobs.length - 1];
      if (savedJob) {
        originalPdfUrlRef.current = savedJob.pdfUrl || null;
      }
      if (data.published) {
        window.gtag('event', 'job_posted', {});
      }
      // Read status from the saved job, not local closure — the just-saved value
      // is fresher than what's in component state at this point.
      const parsePending = isParsePending(savedJob?.jobDescriptionStatus || state.jobDescriptionStatus);
      const successText = initialData
        ? (parsePending ? "Changes saved — we'll keep extracting text in the background." : 'Changes Saved!')
        : (parsePending ? "Job posted — we'll keep extracting text in the background." : 'Job posted!');
      feedback.snackbar({ text: successText, type: "success" });

      // Clear unsaved changes flag and let future parse results sync through.
      setStateInternal({ userEditedDescription: false });
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("JobForm error", error);
      feedback.snackbar({ text: parseGraphQLError(error), type: "error" });
    }
  }

  const handleViewForm = () => {
    feedback.alert({
      // title: "View Questions",
      component: <FormRenderer form={{ questions: state.questions }} isPreview />,
      confirmButtonText: "Close",
      maxWidth: "md",
      fullWidth: true,
      clickAwayClose: true,
      removePadding: true,
    });
  }

  const handleShowEditForm = () => setShowFormEdit(true);

  const handleCloseForm = async () => {
    if (initialData) {
      try {
        let data = { ...getStateFromInitialData(initialData) };
        data = formatData(data, isAtsMode);
        const { data: { postJob: newJob } } = await submit(({ variables: { job: data } }));
        console.log("newJob", newJob);
        feedback.snackbar({ text: initialData ? "Screening Questions saved!" : "Job posted!", type: "success" });

        // Clear unsaved changes flag
        setHasUnsavedChanges(false);
      } catch (error) {
        console.error("JobForm error", error);
        feedback.snackbar({ text: parseGraphQLError(error), type: "error" });
      }
    }
    setShowFormEdit(false);
  }
  const handleShowEditInterview = () => setShowInterviewEdit(true);
  const handleCloseInterview = async () => {
    if (initialData) {
      try {
        let data = { ...getStateFromInitialData(initialData) };
        data = formatData(data, isAtsMode);
        const { data: { postJob: newJob } } = await submit(({ variables: { job: data } }));
        console.log("newJob", newJob);
        feedback.snackbar({ text: initialData ? "Interview Questions saved!" : "Job posted!", type: "success" });

        // Clear unsaved changes flag
        setHasUnsavedChanges(false);
      } catch (error) {
        console.error("JobForm error", error);
        feedback.snackbar({ text: parseGraphQLError(error), type: "error" });
      }
    }
    setShowInterviewEdit(false);
  }

  const handlePdfChange = async (file) => {
    const pdfUpload = await uploadPdf(file);
    if (pdfUpload) {
      setState({ pdfUrl: pdfUpload, userEditedDescription: false });
    }
  };

  const handlePdfRemove = () => {
    const baseline = originalPdfUrlRef.current || '';
    if (state.pdfUrl && state.pdfUrl !== baseline) {
      setState({ pdfUrl: baseline });
    } else {
      setState({ pdfUrl: '' });
    }
    // Clear the FileUpload card (filename + progress) — the hook's abort handler
    // only fires for in-flight uploads.
    resetUpload();
  };

  const handleDescriptionTextChange = (value) => {
    setState({ jobDescription: value, userEditedDescription: true });
  };

  const handleChange = ({ target: { name, value, checked, type } }) => {
    setState({ [name]: type === "checkbox" ? checked : value });
  }

  useEffect(() => {
    if (!initialData || !templates.length) return;
    if (loadedJobIdRef.current === initialData._id) return;
    setStateInternal(getStateFromInitialData(initialData, defaultInterviewQuestions));
    setHasUnsavedChanges(false);
    loadedJobIdRef.current = initialData._id;
    originalPdfUrlRef.current = initialData.pdfUrl || null;
  }, [initialData, templates, defaultInterviewQuestions]);

  // Live sync of server-driven fields (parse status/text) — preserves the
  // user's in-flight textarea edits.
  const remoteJobId = initialData?._id;
  const remoteDescription = initialData?.jobDescription;
  const remoteStatus = initialData?.jobDescriptionStatus;
  const remoteError = initialData?.jobDescriptionError;
  const remoteHasJobDescription = initialData?.hasJobDescription;
  useEffect(() => {
    if (!remoteJobId) return;
    if (loadedJobIdRef.current !== remoteJobId) return;
    const updates = {
      jobDescriptionStatus: remoteStatus,
      jobDescriptionError: remoteError,
      hasJobDescription: remoteHasJobDescription,
    };
    if (!state.userEditedDescription && remoteDescription) {
      updates.jobDescription = remoteDescription;
    }
    setStateInternal(updates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteJobId, remoteDescription, remoteStatus, remoteError, remoteHasJobDescription]);

  useJobParsePolling({
    status: state.jobDescriptionStatus,
    refetch,
    jobId: initialData?._id || null,
  });

  // Surface a snackbar the first time we observe the status flip to 'failed' —
  // the inline Alert may be off-screen on a long form. (C2)
  // Skip the snackbar if the failure is for a PDF the user has already replaced
  // (cancel mode) or removed.
  const prevParseStatusRef = useRef(null);
  useEffect(() => {
    const status = state.jobDescriptionStatus;
    const pdfMatchesBaseline = !!state.pdfUrl && state.pdfUrl === originalPdfUrlRef.current;
    if (
      prevParseStatusRef.current &&
      prevParseStatusRef.current !== 'failed' &&
      status === 'failed' &&
      pdfMatchesBaseline
    ) {
      feedback.snackbar({
        text: `PDF parsing failed${state.jobDescriptionError ? `: ${state.jobDescriptionError}` : ''}`,
        type: 'error',
      });
    }
    prevParseStatusRef.current = status;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.jobDescriptionStatus, state.jobDescriptionError]);

  useEffect(() => {
    // For new jobs, set default interview questions once templates load (first template)
    if (!initialData && jobId === 'new' && templates.length && state.interviewQuestions.length === 0) {
      setStateInternal(prev => ({ ...prev, interviewQuestions: defaultInterviewQuestions }));
      setHasUnsavedChanges(false);
    }
  }, [initialData, jobId, templates, defaultInterviewQuestions, state.interviewQuestions.length]);

  if (isNotFound) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography level="h2" color="danger">Job not found</Typography>
        <Button onClick={() => navigate('/jobs')} sx={{ mt: 2 }}>
          Back to Jobs
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography level="h2" sx={{ mb: 1, fontWeight: 700 }}>
              {initialData ? 'Edit Job' : 'New Job'}
            </Typography>
            <Typography level="body-md" color="neutral">
              {initialData ? 'Update job details, questions, and settings' : 'Create a new job posting with screening questions'}
            </Typography>
          </Box>
          {initialData && (
            <Chip
              variant="soft"
              color={initialData.published ? 'success' : 'neutral'}
              size="lg"
            >
              {initialData.published ? 'Published' : 'Draft'}
            </Chip>
          )}
        </Box>
      </Box>

      <Stack spacing={3}>
        {/* Job Title */}
        <Card>
          <CardContent>
            <Typography level="title-md" sx={{ mb: 2 }}>Job Information</Typography>
            <Stack component="form" id='job-form' onSubmit={handleSubmit} gap={2}>
              <FormControl required>
                <FormLabel>Job Title</FormLabel>
                <Input
                  placeholder="e.g. Sales Development Representative"
                  value={state.title}
                  onChange={handleChange}
                  name="title"
                  size="lg"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Comment (internal use only)</FormLabel>
                <Input
                  placeholder="Internal notes (not visible to candidates)"
                  value={state.comment}
                  onChange={handleChange}
                  name="comment"
                  size="lg"
                />
              </FormControl>
              {!initialData &&
                <DefaultInterviewQuestionsSelector
                  interviewQuestions={state.interviewQuestions}
                  setInterviewQuestions={(questions) => setState({ interviewQuestions: questions })}
                />
              }
            </Stack>
          </CardContent>
        </Card>

        {/* Job Description */}
        <Card>
          <CardContent>
            <Typography level="title-md" sx={{ mb: 2 }}>Job Description</Typography>
            <JobDescriptionInput
              pdfUrl={state.pdfUrl}
              originalPdfUrl={originalPdfUrlRef.current}
              jobDescription={state.jobDescription}
              jobDescriptionStatus={state.jobDescriptionStatus}
              jobDescriptionError={state.jobDescriptionError}
              uploadObject={uploadObject}
              onPdfChange={handlePdfChange}
              onPdfRemove={handlePdfRemove}
              onTextChange={handleDescriptionTextChange}
              disabled={posting}
              userEdited={state.userEditedDescription}
            />
          </CardContent>
        </Card>

        {/* Screening Questions */}
        {!isAtsMode && (
          <Card>
            <CardContent>
              <Typography level="title-md" sx={{ mb: 1 }}>Screening Questions</Typography>
              <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                Pre-filter candidates with custom questions
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Chip variant="soft" color="primary">
                  {state.questions.length} Questions
                </Chip>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="solid"
                  onClick={handleShowEditForm}
                  startDecorator={<EditIcon />}
                >
                  Edit Questions
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleViewForm}
                  startDecorator={<ViewIcon />}
                >
                  Preview
                </Button>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Interview Questions */}
        <Card>
          <CardContent>
            <Typography level="title-md" sx={{ mb: 1 }}>Interview Questions</Typography>
            <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
              Set up video interview questions
            </Typography>
            <Stack sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, maxWidth: '100%' }} direction="row" flexWrap="wrap">
              <Chip variant="soft" color="primary">
                {state.interviewQuestions.map(x => x.questions.length).reduce((a, b) => a + b, 0)} Questions
              </Chip>
              <Chip variant="soft" color="primary" sx={{ maxWidth: '100%' }}>
                Using {interviewQuestionsTemplate}
              </Chip>
            </Stack>
            <Button
              variant="solid"
              color="primary"
              onClick={handleShowEditInterview}
              startDecorator={<EditIcon />}
            >
              Edit Questions
            </Button>
          </CardContent>
        </Card>

        {/* Publishing Settings */}
        <Card>
          <CardContent>
            <Typography level="title-md" sx={{ mb: 2 }}>Publishing Settings</Typography>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 'sm'
            }}>
              <Box>
                <Typography level="title-sm">Public Job URL</Typography>
                <Typography level="body-sm" color="neutral">
                  {state.published ? 'Job is publicly accessible' : 'Job is in draft mode'}
                </Typography>
              </Box>
              <Switch
                checked={!!state.published}
                onChange={handleChange}
                name="published"
                color={state.published ? 'success' : 'neutral'}
                slotProps={{
                  input: { name: "published" }
                }}
              />
            </Box>

            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 'sm',
              mt: 2
            }}>
              <Box>
                <Typography level="title-sm">Hide Company Name</Typography>
                <Typography level="body-sm" color="neutral">
                  The company name will be {state.hideClientName ? 'hidden' : 'visible'} for candidates applying to this job
                </Typography>
              </Box>
              <Switch
                checked={!!state.hideClientName}
                onChange={handleChange}
                name="hideClientName"
                color={state.hideClientName ? 'success' : 'neutral'}
                slotProps={{
                  input: { name: "hideClientName" }
                }}
              />
            </Box>

            {(jobId && jobId !== "new") && (
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                {initialData?.published && state.published && (
                  <Button
                    variant="soft"
                    endDecorator={<ContentCopy />}
                    sx={{ flex: 1 }}
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/company/${user.client._id}/job/${jobId}/intro`);
                      trackCopyJobUrl(user.client._id, jobId, state.title);
                      feedback.snackbar({ text: "Job URL copied to clipboard!", type: "success" });
                    }}
                  >
                    Copy Job URL
                  </Button>
                )}
                <Button
                  variant="soft"
                  component="a"
                  href={`${window.location.origin}/company/${user.client._id}/job/${jobId}/intro`}
                  target="_blank"
                  rel="noopener noreferrer"
                  endDecorator={<OpenInNew />}
                  sx={{ flex: 1 }}
                  onClick={() => trackOpenDemo(user.client._id, jobId)}
                >
                  Open Demo
                </Button>
              </Stack>
            )}
          </CardContent>
        </Card>
        {jobId && jobId !== "new" &&
          <>
            <ATSJobLinker job={state} providerId="ashby" />
            <ATSJobLinker job={state} providerId="greenhouse" />
          </>
        }
      </Stack>

      {/* Actions */}
      <Box sx={{
        mt: 4,
        pt: 3,
        borderTop: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 2
      }}>
        {(jobId && jobId !== "new") && (
          <Button
            variant="plain"
            color="danger"
            loading={deleting}
            onClick={handleDeleteClick}
            size="sm"
            sx={{
              fontWeight: 400,
              '&:hover': {
                bgcolor: 'danger.50',
                color: 'danger.600'
              }
            }}
          >
            Delete
          </Button>
        )}

        <Button
          size="lg"
          variant="solid"
          color="primary"
          type='submit'
          form='job-form'
          loading={posting}
          sx={{
            minWidth: 140,
            fontWeight: 600,
            px: 3
          }}
        >
          {initialData ? 'Save Changes' : 'Create Job'}
        </Button>
      </Box>

      {/* Modals */}
      <Modal open={showFormEdit}>
        <ModalDialog layout='fullscreen' sx={{ maxWidth: '100%', minWidth: '100%', minHeight: '100%', overflow: 'auto', p: 0 }}>
          <FormConstructor questions={state.questions} setQuestions={questions => setState({ questions })} onClose={handleCloseForm} />
        </ModalDialog>
      </Modal>

      <Modal open={showInterviewEdit} onClose={handleCloseInterview}>
        <ModalDialog layout='fullscreen' sx={{ maxWidth: '100%', minWidth: '100%', minHeight: '100%', overflow: 'auto', p: 0 }}>
          <InterviewQuestionsEditor questions={state.interviewQuestions} setQuestions={questions => setState({ interviewQuestions: questions })} onClose={handleCloseInterview} />
        </ModalDialog>
      </Modal>
    </Box>
  );
}

export default Job;
